import { useState } from 'react';
import { useLoaderData } from 'react-router';

import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    DetailSummaryRow,
    ReadMoreText,
    stripHtml,
} from '~/ui-rocco/pages/detail-shared';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';

/**
 * Mobile overview introduction (Figma §2) — the company summary, then the
 * Services pills beside the Locations-we-service list.
 *
 * Mobile only. On desktop all three are carried by the profile summary header
 * under the banner, which every tab shares, so the overview runs straight from
 * the tab bar into its first content block. Mobile has no such header yet, and
 * keeps them here until it does.
 */
export const ProfessionalSummarySection = () => {
    const { professionalOverview } = useLoaderData<typeof loader>();
    // Preview-aware: the same payload under the public route and the Business
    // Manager preview, with any in-flight edit patch applied.
    const layoutData = useProfessionalLayoutData();
    const [showAllLocations, setShowAllLocations] = useState(false);

    const summary = professionalOverview.BlockSummary
        ? stripHtml(professionalOverview.BlockSummary)
        : undefined;

    const services: CategoryPill[] = (professionalOverview.Services ?? [])
        .slice(0, 8)
        .map((service) => ({ label: service }))
        .filter((pill) => pill.label !== '');

    const locations = Array.from(
        new Set(
            (layoutData?.initial?.LibraryLocations ?? [])
                .filter((location) => !location.LocationHidden)
                .map((location) => location.City)
                .filter((city): city is string => Boolean(city)),
        ),
    );

    // Big franchises (e.g. Resene) service dozens of cities — show the first few
    // and reveal the rest behind a toggle so the list doesn't run the page long.
    const LOCATION_LIMIT = 5;
    const visibleLocations = showAllLocations
        ? locations
        : locations.slice(0, LOCATION_LIMIT);
    const hiddenLocationCount = locations.length - LOCATION_LIMIT;

    return (
        <div className="md:hidden">
            <DetailSummaryRow
                // The profile's ground, as the desktop row beside this one
                // already takes: the shared row paints white by default, and
                // on this page that left the opening statement as a white
                // band between the grey above it and the grey below.
                className="bg-ground"
                // The CTAs this aside used to hold live in the mobile
                // social-profile header above.
                aside={null}
                description={
                    summary ? (
                        <ReadMoreText
                            text={summary}
                            lines={3}
                            // Read more sends the reader to the About
                            // (/info) tab rather than expanding a wall of
                            // text in the overview.
                            anchorHref={`${professionalOverview.Link}/info`}
                            preventScrollReset
                            // font-medium matches the About tab's rendering
                            // of this same summary copy (its intro block
                            // above the accordion).
                            className="font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#181818] max-md:text-lg md:text-[2.0625rem]"
                        />
                    ) : undefined
                }
            />
            {services.length > 0 || locations.length > 0 ? (
                <div className="mx-auto flex w-full max-w-[120rem] bg-ground max-md:flex-col max-md:gap-6 max-md:px-6 max-md:pt-8 md:gap-[7.2rem] md:px-[3rem]">
                    {services.length > 0 ? (
                        <CategoryPills
                            label="Services:"
                            items={services}
                            // Static tags, not links — default cursor (not the
                            // text I-beam), in a fixed 350px (18.229vw @1920) box.
                            className="cursor-default md:w-[21.875rem]"
                            labelClassName="md:text-[0.9375rem]"
                        />
                    ) : null}
                    {locations.length > 0 ? (
                        <div className="min-w-0">
                            <p className="m-0 font-sans leading-snug text-[#767676] max-md:text-sm md:text-[0.9375rem]">
                                Locations we service
                            </p>
                            {/* Expanded, the full list can run 40+ cities long, so
                                on desktop it flows into 3 balanced columns
                                (multicol fills top-to-bottom, last column short);
                                collapsed and mobile stay a single stack. */}
                            <ul
                                className={`m-0 mt-[1.2rem] list-none p-0 max-md:mt-3 ${
                                    showAllLocations
                                        ? 'md:columns-3 md:[column-gap:3rem]'
                                        : ''
                                }`}
                            >
                                {visibleLocations.map((city) => (
                                    <li
                                        key={city}
                                        // Uppercase ~12px to match the Figma (node
                                        // 2354:55672) — mirrors the Services pills
                                        // beside it, not the old 20px list.
                                        className="mb-[0.1875rem] break-inside-avoid font-sans leading-normal text-[#1f1f21] uppercase max-md:mb-1 max-md:text-xs md:text-[0.75rem]"
                                    >
                                        {city}
                                    </li>
                                ))}
                            </ul>
                            {hiddenLocationCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowAllLocations((v) => !v)
                                    }
                                    className="mt-[0.6rem] w-fit font-sans leading-normal text-[#767676] uppercase underline underline-offset-2 transition-colors hover:text-[#1f1f21] max-md:mt-1 max-md:text-xs md:text-[0.75rem]"
                                >
                                    {showAllLocations
                                        ? 'Show less'
                                        : `+${hiddenLocationCount} more`}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
